import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  fetchProjectWorkflows,
  fetchWorkflows,
  setProjectDeliveryWorkflow,
} from '@/features/workflow/api'
import type { ApiError } from '@/lib/api/client'
import type { ProjectWorkflows, Workflow } from '@/types/workflow'

/**
 * The two lifecycles a project runs, side by side and clearly labelled.
 *
 * Phase 1 pointed both at the same workflow and put sales stages on a task
 * board. Naming each for what it holds is the correction.
 */
export function WorkflowPanel({ projectKey }: { projectKey: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const assignment = useQuery<ProjectWorkflows, ApiError>({
    queryKey: ['project-workflows', projectKey],
    queryFn: () => fetchProjectWorkflows(projectKey),
    retry: false,
  })

  const workflows = useQuery<Workflow[], ApiError>({
    queryKey: ['workflows'],
    queryFn: fetchWorkflows,
    retry: false,
  })

  const change = useMutation<ProjectWorkflows, ApiError, string>({
    mutationFn: (id) => setProjectDeliveryWorkflow(projectKey, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project-workflows', projectKey] })
      void queryClient.invalidateQueries({ queryKey: ['board', projectKey] })
      void queryClient.invalidateQueries({ queryKey: ['timeline', projectKey] })
    },
  })

  if (assignment.data === undefined) return null

  // Only workflows that supply board columns can be a delivery workflow; the
  // engagement pipeline is not a set of columns and must not appear here.
  const candidates = (workflows.data ?? []).filter(
    (workflow) => workflow.appliesTo !== 'project',
  )

  return (
    <section className="grid gap-3 sm:grid-cols-2">
      <div className="border-line bg-surface rounded-md border p-4">
        <h3 className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
          {t('workflow.delivery')}
        </h3>
        <p className="text-ink-900 mt-1 font-medium">
          {assignment.data.delivery?.name ?? t('workflow.none')}
        </p>
        <p className="text-ink-500 mt-1 text-xs">{t('workflow.deliveryExplainer')}</p>

        {candidates.length === 0 ? null : (
          <label className="mt-3 block">
            <span className="sr-only">{t('workflow.switch')}</span>
            <select
              value={assignment.data.delivery?.id ?? ''}
              disabled={change.isPending}
              onChange={(event) => {
                if (event.target.value !== '') change.mutate(event.target.value)
              }}
              className="border-line bg-surface w-full rounded border px-2.5 py-1.5 text-sm"
            >
              <option value="">—</option>
              {candidates.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {change.data?.cardsMoved === undefined ? null : (
          <p className="text-brand-700 mt-2 text-xs">
            {t('workflow.switched', { moved: change.data.cardsMoved })}
          </p>
        )}

        {change.error === null ? null : (
          <p className="mt-2 text-xs text-[color:var(--color-state-blocked)]">
            {change.error.message}
          </p>
        )}
      </div>

      <div className="border-line bg-surface rounded-md border p-4">
        <h3 className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
          {t('workflow.engagement')}
        </h3>
        <p className="text-ink-900 mt-1 font-medium">
          {assignment.data.engagement?.name ?? t('workflow.none')}
        </p>
        <p className="text-ink-500 mt-1 text-xs">{t('workflow.engagementExplainer')}</p>

        {assignment.data.stage === null ? null : (
          <p className="border-brand-300 bg-brand-50 text-brand-700 mt-3 inline-block rounded border px-2 py-0.5 font-mono text-xs">
            {assignment.data.stage.name}
          </p>
        )}
      </div>
    </section>
  )
}
